import type { DefaultOptionType } from 'rc-select/lib/Select';
import React from 'react';

import DateTime from '~/components/Columns/DateTime';
import { prefetchOptions, toLabel } from '~/components/Columns/helpers';

import { DateFilterPresets } from '~/types/components';
import {
  type CollectionColumnDefinition,
  type ColumnDefinition,
  type ItemColumnDefinition,
  ModelColumns,
} from '~/types/components';
import type { BounceReason, Contact, Sms, PointageResult, Tag } from '~/types/models';
import { bounceReasonLabel, pointageResultLabel } from '~/types/models';

import { labelsToOptions } from '~/helpers';

const smsContactColumn: ItemColumnDefinition<Sms, Contact> = {
  title: 'Contact',
  key: 'contact',
  sortable: false,
  type: 'item',
  render: (value) => {
    return (
      <span>{value?.firstName} {value?.lastName}</span>
    );
  },
};

const smsTagsColumn: CollectionColumnDefinition<Sms, Tag> = {
  title: 'Étiquettes',
  key: 'tags',
  mapOptions(items: Tag[]): DefaultOptionType[] {
    return items.map((i) => ({
      value: i.id,
      key: i.id,
      label: i.name,
    }));
  },
  prefetchOptions: prefetchOptions<Tag>('tags'),
  sortable: false,
  toLabel: toLabel('tags', 'Étiquette'),
  type: 'collection',
  render: (value) => {
    if (!Array.isArray(value)) {
      return <em>Chargement...</em>;
    }

    if (value.length === 0) {
      return <em>Aucune étiquette</em>;
    }

    return (
      <ul style={{ margin: 0, padding: 0 }}>
        {value.map((g) => (
          <li key={g.id}>{g.name}</li>
        ))}
      </ul>
    );
  },
};

export const Columns: Array<ColumnDefinition<Sms>> = [
  {
    key: 'bounced',
    options: labelsToOptions(bounceReasonLabel),
    readOnly: true,
    render: (value: BounceReason) => <span>{bounceReasonLabel[value] ?? 's. o.'}</span>,
    sortable: true,
    title: 'Refusé',
    type: 'enum',
  },
  {
    key: 'answered',
    presets: DateFilterPresets.Past,
    readOnly: true,
    render: (v) => <DateTime value={v} />,
    sortable: true,
    title: 'Répondu',
    type: 'datetime',
  },
  {
    key: 'answer',
    readOnly: true,
    sortable: true,
    title: 'Réponse(s)',
    type: 'string',
    render: (v: string | null) => <pre style={{ margin: 0 }}>{v === null ? '' : v}</pre>,
  },
  {
    key: 'lastResult',
    options: labelsToOptions(pointageResultLabel),
    readOnly: true,
    render: (value: PointageResult) => <span>{pointageResultLabel[value] ?? 's. o.'}</span>,
    sortable: true,
    title: 'Dernier pointage',
    type: 'enum',
  },
  {
    key: 'sent',
    presets: DateFilterPresets.Past,
    render: (v) => <DateTime value={v} />,
    sortable: true,
    title: 'Envoi',
    type: 'datetime',
  },
  {
    key: 'toNumber',
    readOnly: true,
    sortable: true,
    title: 'Numéro',
    type: 'string',
  },
  smsContactColumn,
  smsTagsColumn,
  ...ModelColumns,
];
